import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Please check your .env.local file.\n' +
    'Required variables:\n' +
    '  - REACT_APP_SUPABASE_URL\n' +
    '  - REACT_APP_SUPABASE_ANON_KEY'
  );
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper function to get current user
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Helper function to get user profile
export const getUserProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  
  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
  
  return data;
};

// Helper function to check if username is available
export const checkUsernameAvailable = async (username) => {
  const { data, error } = await supabase
    .rpc('is_username_available', { p_username: username });
  
  if (error) {
    console.error('Error checking username:', error);
    return false;
  }
  
  return data;
};

// Helper function to format time for display (MM:SS)
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Helper function to parse time string to seconds
export const parseTimeToSeconds = (timeString) => {
  const [mins, secs] = timeString.split(':').map(Number);
  return (mins * 60) + secs;
};

// Helper function to get current date in YYYY-MM-DD format (UTC)
export const getCurrentDate = () => {
  return new Date().toISOString().split('T')[0];
};

// Helper function to get yesterday's date in YYYY-MM-DD format (UTC)
export const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

// =====================================================
// LEADERBOARD QUERIES
// =====================================================

/**
 * Fetch leaderboard for a specific difficulty and date
 * Returns eligible solves (standard mode, ≤2 mistakes) sorted by time
 */
export const getLeaderboard = async (difficulty, date) => {
  try {
    const { data, error } = await supabase
      .from('solves')
      .select(`
        *,
        profiles:user_id (
          username,
          is_free_founder,
          is_paid_founder
        )
      `)
      .eq('difficulty', difficulty)
      .eq('puzzle_date', date)
      .eq('is_eligible', true)
      .order('time_seconds', { ascending: true });

    if (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }

    // Transform data to match expected format
    return data.map((solve, index) => ({
      rank: index + 1,
      username: solve.profiles?.username || 'Anonymous',
      time: formatTime(solve.time_seconds),
      timeSeconds: solve.time_seconds,
      mistakes: solve.mistakes,
      founderTier: solve.profiles?.is_paid_founder ? 'paid' 
                  : solve.profiles?.is_free_founder ? 'free' 
                  : 'none',
      userId: solve.user_id,
      solvedAt: solve.solved_at
    }));
  } catch (error) {
    console.error('Error in getLeaderboard:', error);
    return [];
  }
};

/**
 * Get top N solvers for a specific difficulty and date
 */
export const getTopSolvers = async (difficulty, date, limit = 5) => {
  const leaderboard = await getLeaderboard(difficulty, date);
  return leaderboard.slice(0, limit);
};

/**
 * Get user's rank on leaderboard
 */
export const getUserRankOnLeaderboard = async (userId, difficulty, date) => {
  const leaderboard = await getLeaderboard(difficulty, date);
  const userIndex = leaderboard.findIndex(entry => entry.userId === userId);
  
  if (userIndex === -1) {
    return null;
  }
  
  return {
    ...leaderboard[userIndex],
    rank: userIndex + 1,
    totalSolvers: leaderboard.length
  };
};

/**
 * Get all-time best solvers (aggregate best times per user per difficulty)
 */
export const getAllTimeBestSolvers = async (difficulty, limit = 50) => {
  try {
    // Get each user's best time for this difficulty
    const { data, error } = await supabase
      .rpc('get_all_time_best', { 
        p_difficulty: difficulty,
        p_limit: limit
      });

    if (error) {
      console.error('Error fetching all-time best:', error);
      // Fallback: Manual aggregation if RPC doesn't exist
      return getAllTimeBestFallback(difficulty, limit);
    }

    return data.map((entry, index) => ({
      rank: index + 1,
      username: entry.username,
      time: formatTime(entry.best_time),
      timeSeconds: entry.best_time,
      founderTier: entry.is_paid_founder ? 'paid' 
                  : entry.is_free_founder ? 'free' 
                  : 'none',
      userId: entry.user_id
    }));
  } catch (error) {
    console.error('Error in getAllTimeBestSolvers:', error);
    return getAllTimeBestFallback(difficulty, limit);
  }
};

/**
 * Fallback for all-time best if RPC doesn't exist
 */
const getAllTimeBestFallback = async (difficulty, limit) => {
  try {
    const { data, error } = await supabase
      .from('solves')
      .select(`
        user_id,
        time_seconds,
        profiles:user_id (
          username,
          is_free_founder,
          is_paid_founder
        )
      `)
      .eq('difficulty', difficulty)
      .eq('is_eligible', true)
      .order('time_seconds', { ascending: true });

    if (error) {
      console.error('Error in fallback query:', error);
      return [];
    }

    // Group by user_id and get best time
    const userBestTimes = new Map();
    
    data.forEach(solve => {
      const userId = solve.user_id;
      const currentBest = userBestTimes.get(userId);
      
      if (!currentBest || solve.time_seconds < currentBest.time_seconds) {
        userBestTimes.set(userId, solve);
      }
    });

    // Convert to array and sort
    const bestTimes = Array.from(userBestTimes.values())
      .sort((a, b) => a.time_seconds - b.time_seconds)
      .slice(0, limit);

    return bestTimes.map((solve, index) => ({
      rank: index + 1,
      username: solve.profiles?.username || 'Anonymous',
      time: formatTime(solve.time_seconds),
      timeSeconds: solve.time_seconds,
      founderTier: solve.profiles?.is_paid_founder ? 'paid' 
                  : solve.profiles?.is_free_founder ? 'free' 
                  : 'none',
      userId: solve.user_id
    }));
  } catch (error) {
    console.error('Error in getAllTimeBestFallback:', error);
    return [];
  }
};

/**
 * Check if user has completed a specific difficulty today
 */
export const hasUserCompletedToday = async (userId, difficulty) => {
  const today = getCurrentDate();
  
  try {
    const { data, error } = await supabase
      .from('solves')
      .select('id')
      .eq('user_id', userId)
      .eq('difficulty', difficulty)
      .eq('puzzle_date', today)
      .limit(1);

    if (error) {
      console.error('Error checking completion:', error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('Error in hasUserCompletedToday:', error);
    return false;
  }
};

/**
 * Get user's completion status for all difficulties today
 */
export const getUserTodayCompletions = async (userId) => {
  const today = getCurrentDate();
  
  try {
    const { data, error } = await supabase
      .from('solves')
      .select('difficulty')
      .eq('user_id', userId)
      .eq('puzzle_date', today);

    if (error) {
      console.error('Error fetching completions:', error);
      return { easy: false, hard: false, master: false };
    }

    const completions = {
      easy: false,
      hard: false,
      master: false
    };

    data.forEach(solve => {
      completions[solve.difficulty] = true;
    });

    return completions;
  } catch (error) {
    console.error('Error in getUserTodayCompletions:', error);
    return { easy: false, hard: false, master: false };
  }
};

// =====================================================
// PUZZLE QUERIES
// =====================================================

/**
 * Get today's puzzle for a specific difficulty
 * Returns puzzle data or error object with fallback info
 */
export const getTodaysPuzzle = async (difficulty) => {
  try {
    const { data, error } = await supabase
      .rpc('get_todays_puzzle', { p_difficulty: difficulty });

    if (error) {
      console.error('Error fetching puzzle:', error);
      return { success: false, error: 'database_error', data: null };
    }

    // Check if function returned an error response
    if (data && data.success === false) {
      console.warn('Puzzle not available:', data);
      return data;
    }

    return data;
  } catch (error) {
    console.error('Exception fetching puzzle:', error);
    return { success: false, error: 'exception', data: null };
  }
};

/**
 * Get puzzle pool status (admin only)
 */
export const getPuzzlePoolStatus = async () => {
  try {
    const { data, error } = await supabase
      .rpc('check_puzzle_pool_status');

    if (error) {
      console.error('Error fetching pool status:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Exception fetching pool status:', error);
    return null;
  }
};

/**
 * Add a new puzzle to the library (admin only)
 */
export const addPuzzle = async (puzzleData) => {
  try {
    const { data, error } = await supabase
      .from('puzzles')
      .insert({
        difficulty: puzzleData.difficulty,
        puzzle_grid: puzzleData.puzzleGrid,
        solution_grid: puzzleData.solutionGrid,
        notes: puzzleData.notes || null,
        quality_rating: puzzleData.qualityRating || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding puzzle:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception adding puzzle:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a puzzle from the library (admin only)
 */
export const deletePuzzle = async (puzzleId) => {
  try {
    const { error } = await supabase
      .from('puzzles')
      .delete()
      .eq('id', puzzleId);

    if (error) {
      console.error('Error deleting puzzle:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Exception deleting puzzle:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all puzzles for a specific difficulty (admin only)
 */
export const getPuzzlesByDifficulty = async (difficulty) => {
  try {
    const { data, error } = await supabase
      .from('puzzles')
      .select('*')
      .eq('difficulty', difficulty)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching puzzles:', error);
      return [];
    }

    return data;
  } catch (error) {
    console.error('Exception fetching puzzles:', error);
    return [];
  }
};

/**
 * Update puzzle config (admin only)
 */
export const updatePuzzleConfig = async (key, value) => {
  try {
    const { error } = await supabase
      .from('puzzle_config')
      .update({ value, updated_at: new Date().toISOString() })
      .eq('key', key);

    if (error) {
      console.error('Error updating config:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Exception updating config:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Manually trigger puzzle rotation (admin only - for testing)
 */
export const manuallyRotatePuzzles = async () => {
  try {
    const { data, error } = await supabase
      .rpc('rotate_daily_puzzles');

    if (error) {
      console.error('Error rotating puzzles:', error);
      return { success: false, error: error.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Exception rotating puzzles:', error);
    return { success: false, error: error.message };
  }
};
